import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ToolInfo, PdfImageModeInfo, ToolCategory, UnifiedToolInfo } from '../types';
import { searchTools } from '../utils/toolSearch';
import { 
    SearchIcon, CopilotIcon, ColorfulCurrencyIcon, ColorfulImageCompressorIcon, 
    ColorfulOcrIcon, ColorfulPdfSplitIcon, ColorfulPdfImageIcon, 
    ColorfulBarcodeIcon, ColorfulVoiceToTextIcon, LockClosedIcon, SparklesIcon, CheckIcon, BrightnessIcon,
    LayersIcon, ImageIcon, DownloadIcon, SwapIcon, ArrowLeftIcon,
    ColorfulPdfEditorIcon, ColorfulImageEditorIcon, ColorfulYoutubeTrendIcon, ColorfulPdfSecurityIcon,
    ColorfulJsonIcon, ColorfulAgeCalculatorIcon, ChevronDownIcon, CodeBracketIcon, TextIcon, ColorfulFacebookIcon
} from './Icons';

const IconSphere: React.FC = () => {
  return (
    <div className="relative w-[380px] h-[380px] sm:w-[460px] sm:h-[460px] flex items-center justify-center select-none my-6">
      {/* Outer ambient pulsing aura */}
      <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500/25 via-sky-500/15 to-indigo-600/20 rounded-full blur-3xl -z-10 animate-pulse" style={{ animationDuration: '4s' }} />

      {/* Orbit Track 1 (Outer - 410px) with subtle glow */}
      <div className="absolute w-[360px] h-[360px] sm:w-[410px] sm:h-[410px] rounded-full border border-dashed border-cyan-500/20 dark:border-cyan-400/25 pointer-events-none shadow-[0_0_20px_rgba(6,182,212,0.08)]" />
      
      {/* Orbit Track 2 (Inner - 230px) */}
      <div className="absolute w-[200px] h-[200px] sm:w-[230px] sm:h-[230px] rounded-full border border-dashed border-blue-500/25 dark:border-blue-400/30 pointer-events-none" />

      {/* Center Holographic AI Reactor Core */}
      <div className="relative z-20 flex flex-col items-center justify-center">
        <div className="absolute w-32 h-32 rounded-full bg-gradient-to-br from-cyan-400 via-blue-500 to-indigo-600 blur-2xl opacity-50 animate-pulse" />
        
        <div className="relative w-26 h-26 sm:w-30 sm:h-30 rounded-full bg-slate-950/95 border-2 border-cyan-400/60 flex flex-col items-center justify-center p-3.5 shadow-[0_0_50px_rgba(6,182,212,0.35)] backdrop-blur-xl group">
          <div className="p-3 bg-gradient-to-tr from-cyan-400 via-blue-500 to-indigo-600 rounded-2xl shadow-lg shadow-cyan-500/50 group-hover:scale-110 transition-transform duration-500">
            <CopilotIcon className="w-8 h-8 text-white" />
          </div>
          <span className="mt-1.5 text-[10px] font-black tracking-widest text-cyan-300 uppercase">
            AI Engine
          </span>
        </div>

        <div className="absolute -bottom-4 bg-slate-900/90 dark:bg-slate-900/95 backdrop-blur-md px-3 py-1 rounded-full border border-cyan-500/40 shadow-lg shadow-cyan-950/50 flex items-center gap-1.5 whitespace-nowrap">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
          <span className="text-[10px] font-extrabold text-cyan-200 tracking-wide">140+ Client Tools</span>
        </div>
      </div>

      {/* Inner Orbit (Clockwise 30s) */}
      <div className="absolute inset-0 z-10" style={{ animation: 'rotate-clockwise 30s linear infinite' }}>
        {[
          { Icon: ColorfulPdfEditorIcon, angle: 0, label: 'PDF Editor' },
          { Icon: ColorfulImageEditorIcon, angle: 90, label: 'AI Image' },
          { Icon: ColorfulYoutubeTrendIcon, angle: 180, label: 'YouTube SEO' },
          { Icon: ColorfulPdfSecurityIcon, angle: 270, label: 'Security' },
        ].map(({ Icon, angle, label }, idx) => (
          <div 
            key={idx}
            className="absolute top-1/2 left-1/2 w-12 h-12 -mt-6 -ml-6"
            style={{
              transform: `rotate(${angle}deg) translateX(110px) rotate(-${angle}deg)`
            }}
          >
            <div 
              className="w-12 h-12 rounded-2xl bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-700/80 shadow-xl shadow-slate-900/10 dark:shadow-cyan-950/40 flex items-center justify-center hover:scale-125 transition-transform duration-300 backdrop-blur-md"
              style={{ animation: 'rotate-counter-clockwise 30s linear infinite' }}
              title={label}
            >
              <Icon className="w-7 h-7" />
            </div>
          </div>
        ))}
      </div>

      {/* Outer Orbit (Counter-Clockwise 45s) */}
      <div className="absolute inset-0 z-10" style={{ animation: 'rotate-counter-clockwise 45s linear infinite' }}>
        {[
          { Icon: ColorfulImageCompressorIcon, angle: 0, label: 'Compress PDF' },
          { Icon: ColorfulJsonIcon, angle: 60, label: 'JSON Formatter' },
          { Icon: ColorfulOcrIcon, angle: 120, label: 'Smart OCR' },
          { Icon: ColorfulCurrencyIcon, angle: 180, label: 'Converters' },
          { Icon: ColorfulAgeCalculatorIcon, angle: 240, label: 'Calculators' },
          { Icon: ColorfulVoiceToTextIcon, angle: 300, label: 'Voice to Text' },
        ].map(({ Icon, angle, label }, idx) => (
          <div 
            key={idx}
            className="absolute top-1/2 left-1/2 w-12 h-12 -mt-6 -ml-6"
            style={{
              transform: `rotate(${angle}deg) translateX(200px) rotate(-${angle}deg)`
            }}
          >
            <div 
              className="w-12 h-12 rounded-2xl bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-700/80 shadow-2xl shadow-slate-900/10 dark:shadow-cyan-950/50 flex items-center justify-center hover:scale-125 transition-transform duration-300 backdrop-blur-md"
              style={{ animation: 'rotate-clockwise 45s linear infinite' }}
              title={label}
            >
              <Icon className="w-7 h-7" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ImageComparisonSlider: React.FC = () => {
    const [sliderPos, setSliderPos] = useState(50);
    const containerRef = useRef<HTMLDivElement>(null);
    const isDragging = useRef(false);

    const imageUrl = "https://images.unsplash.com/photo-1501785888041-af3ef285b470?q=80&w=800&auto=format&fit=crop";

    const handleMove = (clientX: number) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const position = ((clientX - rect.left) / rect.width) * 100;
        setSliderPos(Math.max(0, Math.min(100, position)));
    };

    const onTouchMove = (e: TouchEvent) => handleMove(e.touches[0].clientX);
    const onMouseMove = (e: MouseEvent) => handleMove(e.clientX);
    
    const onMouseDown = () => { isDragging.current = true; };
    const onMouseUp = () => { isDragging.current = false; };

    useEffect(() => {
        const handleWindowMove = (e: MouseEvent) => { if (isDragging.current) onMouseMove(e); };
        const handleWindowTouchMove = (e: TouchEvent) => { if (isDragging.current) onTouchMove(e); };
        const handleWindowUp = () => { isDragging.current = false; };

        window.addEventListener('mousemove', handleWindowMove);
        window.addEventListener('mouseup', handleWindowUp);
        window.addEventListener('touchmove', handleWindowTouchMove);
        window.addEventListener('touchend', handleWindowUp);

        return () => {
            window.removeEventListener('mousemove', handleWindowMove);
            window.removeEventListener('mouseup', handleWindowUp);
            window.removeEventListener('touchmove', handleWindowTouchMove);
            window.removeEventListener('touchend', handleWindowUp);
        };
    }, []);

    return (
        <div 
            ref={containerRef}
            className="relative w-full max-w-lg aspect-[4/3] bg-slate-900 rounded-3xl overflow-hidden shadow-2xl border-4 border-white/80 dark:border-slate-800 transition-transform duration-500 group-hover/visual:scale-[1.02] select-none cursor-ew-resize"
            onMouseDown={onMouseDown}
            onTouchStart={onMouseDown}
        >
            <div className="absolute inset-0">
                <img 
                    src={imageUrl} 
                    className="w-full h-full object-cover saturate-150 contrast-125 brightness-110" 
                    alt="Remastered" 
                    draggable="false"
                />
                <div className="absolute top-4 right-4 bg-emerald-500/90 backdrop-blur-md text-white text-[10px] font-black uppercase px-2.5 py-1 rounded-full shadow-lg flex items-center gap-1">
                    <SparklesIcon className="w-3 h-3" /> Remastered (4K HDR)
                </div>
            </div>

            <div 
                className="absolute inset-0 overflow-hidden border-r-2 border-white shadow-[10px_0_30px_rgba(0,0,0,0.4)]"
                style={{ width: `${sliderPos}%` }}
            >
                <img 
                    src={imageUrl} 
                    className="h-full object-cover grayscale brightness-75 contrast-75" 
                    style={{ width: `${100 * (100 / sliderPos)}%`, maxWidth: 'none' }}
                    alt="Original" 
                    draggable="false"
                />
                <div className="absolute top-4 left-4 bg-slate-900/80 backdrop-blur-md text-slate-300 text-[10px] font-bold uppercase px-2.5 py-1 rounded-full shadow-lg">
                    Original RAW
                </div>
            </div>

            <div 
                className="absolute top-0 bottom-0 w-1 bg-white dark:bg-slate-800 cursor-ew-resize z-20 shadow-[0_0_15px_rgba(255,255,255,0.8)]"
                style={{ left: `${sliderPos}%`, transform: 'translateX(-50%)' }}
            >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white dark:bg-slate-900 rounded-full shadow-2xl flex items-center justify-center border-2 border-cyan-500 transition-transform hover:scale-110">
                    <SwapIcon className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                </div>
            </div>
        </div>
    );
};

const ToolCard: React.FC<{ tool: UnifiedToolInfo; navigateTo: (path: string) => void; }> = ({ tool, navigateTo }) => {
    const Icon = tool.icon;
    const handleNavigate = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
        e.preventDefault();
        navigateTo(path);
    };

    return (
        <a 
            href={`/tool/${tool.id}`}
            onClick={(e) => handleNavigate(e, `/tool/${tool.id}`)}
            className="group relative flex flex-col justify-between h-full bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm border border-slate-200/80 dark:border-slate-800 rounded-2xl p-4 sm:p-5 shadow-sm hover:shadow-xl dark:hover:shadow-cyan-950/30 hover:border-cyan-500/50 dark:hover:border-cyan-500/50 transition-all duration-300 hover:-translate-y-1 hover:scale-[1.01]"
        >
            <div>
                <div className="flex items-center justify-between gap-2 mb-3.5">
                    <div className="w-11 h-11 bg-slate-50 dark:bg-slate-800 rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 border border-slate-100 dark:border-slate-700/60 shadow-sm flex-shrink-0">
                        {Icon ? <Icon className="w-7 h-7" /> : <div className="w-7 h-7 bg-cyan-600 rounded-lg" />}
                    </div>
                    {tool.category && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 group-hover:bg-cyan-50 dark:group-hover:bg-cyan-950/80 group-hover:text-cyan-700 dark:group-hover:text-cyan-300 transition-colors truncate max-w-[130px]">
                            {tool.category}
                        </span>
                    )}
                </div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white mb-1.5 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors line-clamp-1 text-left">
                    {tool.label}
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed line-clamp-2 min-h-[2rem] text-left">
                    {tool.description && tool.description.trim() ? tool.description : 'Fast, reliable, and secure browser tool.'}
                </p>
            </div>

            <div className="mt-3.5 pt-2.5 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between text-[11px] font-semibold text-slate-400 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">
                <span>Launch Tool</span>
                <div className="w-5 h-5 rounded-full bg-slate-100 dark:bg-slate-800 group-hover:bg-cyan-500 group-hover:text-white flex items-center justify-center transition-all duration-300 group-hover:translate-x-1 shadow-xs">
                    <ArrowLeftIcon className="w-3 h-3 rotate-180" />
                </div>
            </div>
        </a>
    );
};

const PlatformFAQ: React.FC = () => {
    const [openIdx, setOpenIdx] = useState<number | null>(0);

    const faqs = [
        {
            q: "Is ToolsCopilot really 100% free with no hidden subscriptions or limits?",
            a: "Yes, absolutely. All 140+ tools on ToolsCopilot are completely free forever. You don't need to create an account, purchase credits, or enter credit card information. There are zero daily conversion caps."
        },
        {
            q: "How is my privacy protected? Do my confidential files upload to your servers?",
            a: "Your files never leave your computer. ToolsCopilot executes processing 100% locally in your browser memory using WebAssembly (Wasm) and HTML5 Canvas APIs. Sensitive bank statements, contracts, tax files, and private photos never touch remote cloud servers."
        },
        {
            q: "Do I need to install any software, browser extensions, or plugins?",
            a: "No installation is required. Everything runs instantly in any modern web browser—including Google Chrome, Apple Safari, Mozilla Firefox, and Microsoft Edge on desktops, tablets, and phones."
        },
        {
            q: "How fast is browser-based processing compared to cloud converters?",
            a: "It is significantly faster because there are no upload or download network delays. Your device's native CPU processes files instantly, allowing you to compress large PDFs, edit images, or calculate financial metrics in milliseconds."
        },
        {
            q: "Can I use ToolsCopilot tools on my smartphone or tablet?",
            a: "Yes! ToolsCopilot is fully responsive and touch-optimized. You can convert PDFs, resize photos, generate QR codes, and transcribe voice notes seamlessly on iOS, iPadOS, and Android."
        },
        {
            q: "How does the PDF compression preserve text sharpness?",
            a: "Our PDF engine performs smart bicubic downsampling specifically on heavy raster images while leaving vector typography, font definitions, and lines untouched at 100% native vector quality."
        },
        {
            q: "Are calculations for Loan EMI, GST, and Profit Margin accurate?",
            a: "Yes. Our calculators use standard financial banking formulas and compound interest algorithms, tested and verified against official accounting standards."
        },
        {
            q: "How can I suggest a new tool or report an issue?",
            a: "We love feedback! You can reach our engineering team directly via our Contact Us form (/contact-us) or check out our latest feature additions in What's New."
        }
    ];

    const toggleFaq = (idx: number) => {
        setOpenIdx(openIdx === idx ? null : idx);
    };

    return (
        <section className="py-16 sm:py-20 border-t border-slate-200/80 dark:border-slate-800">
            <div className="text-center max-w-3xl mx-auto mb-12">
                <span className="text-cyan-600 dark:text-cyan-400 text-xs font-bold uppercase tracking-wider bg-cyan-50 dark:bg-cyan-950/60 px-4 py-1.5 rounded-full border border-cyan-200 dark:border-cyan-900 shadow-xs">
                    Clear & Transparent
                </span>
                <h2 className="text-3xl sm:text-5xl font-black text-slate-900 dark:text-white mt-4 mb-3 tracking-tight">
                    Frequently Asked Questions
                </h2>
                <p className="text-slate-600 dark:text-slate-400 text-base sm:text-lg">
                    Everything you need to know about our privacy architecture, performance, and unlimited free toolkit.
                </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-3.5 text-left">
                {faqs.map((faq, idx) => {
                    const isOpen = openIdx === idx;
                    return (
                        <div 
                            key={idx}
                            className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
                                isOpen 
                                    ? 'bg-white dark:bg-slate-900 border-cyan-500/50 shadow-lg shadow-cyan-950/10' 
                                    : 'bg-white/70 dark:bg-slate-900/60 border-slate-200/80 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'
                            }`}
                        >
                            <button
                                onClick={() => toggleFaq(idx)}
                                className="w-full p-5 sm:p-6 flex items-center justify-between gap-4 text-left transition-colors"
                                aria-expanded={isOpen}
                            >
                                <h3 className="font-extrabold text-slate-900 dark:text-white text-base sm:text-lg flex items-center gap-3">
                                    <span className="text-cyan-600 dark:text-cyan-400 text-sm font-black w-6 h-6 rounded-lg bg-cyan-50 dark:bg-cyan-950/80 flex items-center justify-center flex-shrink-0">
                                        Q
                                    </span>
                                    {faq.q}
                                </h3>
                                <div className={`w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0 text-slate-500 dark:text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180 bg-cyan-600 text-white dark:bg-cyan-500' : ''}`}>
                                    <ChevronDownIcon className="w-4 h-4" />
                                </div>
                            </button>

                            {isOpen && (
                                <div className="px-5 sm:px-6 pb-6 pt-1 text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed border-t border-slate-100 dark:border-slate-800/80">
                                    <p className="pl-9">{faq.a}</p>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

const WelcomeAboutSection: React.FC<{ navigateTo: (path: string) => void; }> = ({ navigateTo }) => {
    return (
        <section className="py-12 sm:py-16 border-t border-slate-200/80 dark:border-slate-800 text-left space-y-12">
            {/* Header / Intro Banner */}
            <div className="max-w-4xl mx-auto text-center space-y-4">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-50 dark:bg-cyan-950/80 text-cyan-700 dark:text-cyan-300 text-xs font-bold uppercase tracking-wider border border-cyan-200 dark:border-cyan-900 shadow-xs">
                    <SparklesIcon className="w-3.5 h-3.5" /> All-in-One Online Productivity Suite
                </div>
                <h2 className="text-3xl sm:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-tight">
                    Welcome to <span className="bg-gradient-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent">ToolsCopilot</span>
                </h2>
                <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed max-w-3xl mx-auto">
                    ToolsCopilot is the world’s premier <strong>100% free, client-side online web toolkit</strong> built for students, software engineers, content creators, marketers, and business professionals. We eliminate slow server uploads, paywalls, and invasive data tracking by running all computational algorithms directly inside your browser.
                </p>
            </div>

            {/* 7 Comprehensive Category Showcases Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                
                {/* 1. PDF Tools */}
                <div className="bg-white dark:bg-slate-900 p-6 sm:p-7 rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-sm hover:shadow-md transition-all space-y-4 flex flex-col justify-between">
                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-red-50 dark:bg-red-950/80 text-red-600 flex items-center justify-center font-bold text-lg">
                                📄
                            </div>
                            <h3 className="text-lg font-black text-slate-900 dark:text-white">PDF Suite & Security</h3>
                        </div>
                        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                            Complete document management without cloud storage. Compress PDF file size by up to 85%, merge multi-page contracts, split and extract pages, crop margins, convert PDFs to Word, Excel, and PPT, apply optical character recognition (OCR), redact confidential numbers, add electronic signatures, and password-protect or unlock PDF files.
                        </p>
                        <div className="flex flex-wrap gap-1.5 pt-1">
                            {['Compress PDF', 'Merge PDF', 'PDF to Image', 'PDF OCR', 'PDF Redactor', 'Unlock PDF'].map(name => (
                                <span key={name} className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                                    {name}
                                </span>
                            ))}
                        </div>
                    </div>
                    <button 
                        onClick={() => navigateTo('/tool/pdf-compress')} 
                        className="text-xs font-bold text-cyan-600 dark:text-cyan-400 hover:underline flex items-center gap-1 cursor-pointer pt-2"
                    >
                        <span>Explore PDF Tools</span> →
                    </button>
                </div>

                {/* 2. Image & AI Vision */}
                <div className="bg-white dark:bg-slate-900 p-6 sm:p-7 rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-sm hover:shadow-md transition-all space-y-4 flex flex-col justify-between">
                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-indigo-50 dark:bg-indigo-950/80 text-indigo-600 flex items-center justify-center font-bold text-lg">
                                🖼️
                            </div>
                            <h3 className="text-lg font-black text-slate-900 dark:text-white">Image & Graphic Studio</h3>
                        </div>
                        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                            Pro-grade photo editing and optimization right in your browser. Compress PNG, JPG, and WebP images, upscale photos to 4K resolution using AI super-resolution, remove backgrounds automatically, convert raster bitmap images into SVG vectors, blur sensitive areas, and split photos for Instagram 9-grid posts.
                        </p>
                        <div className="flex flex-wrap gap-1.5 pt-1">
                            {['Image Compressor', 'AI Upscaler', 'Background Remover', 'Image to SVG', 'Blur Tool'].map(name => (
                                <span key={name} className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                                    {name}
                                </span>
                            ))}
                        </div>
                    </div>
                    <button 
                        onClick={() => navigateTo('/tool/image-compressor')} 
                        className="text-xs font-bold text-cyan-600 dark:text-cyan-400 hover:underline flex items-center gap-1 cursor-pointer pt-2"
                    >
                        <span>Explore Image Tools</span> →
                    </button>
                </div>

                {/* 3. AI & Writing Tools */}
                <div className="bg-white dark:bg-slate-900 p-6 sm:p-7 rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-sm hover:shadow-md transition-all space-y-4 flex flex-col justify-between">
                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-purple-50 dark:bg-purple-950/80 text-purple-600 flex items-center justify-center font-bold text-lg">
                                🤖
                            </div>
                            <h3 className="text-lg font-black text-slate-900 dark:text-white">AI Writing & Voice</h3>
                        </div>
                        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                            Next-generation NLP and speech utilities. Humanize robotic ChatGPT and AI content to bypass AI detectors with custom tones, detect AI-written text, rephrase articles, transcribe speech to text, and convert articles into natural, fluent voice audio with our anti-freeze Text to Speech engine.
                        </p>
                        <div className="flex flex-wrap gap-1.5 pt-1">
                            {['AI Humanizer', 'AI Detector', 'Article Rewriter', 'Text to Speech', 'Voice to Text'].map(name => (
                                <span key={name} className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                                    {name}
                                </span>
                            ))}
                        </div>
                    </div>
                    <button 
                        onClick={() => navigateTo('/tool/ai-humanizer')} 
                        className="text-xs font-bold text-cyan-600 dark:text-cyan-400 hover:underline flex items-center gap-1 cursor-pointer pt-2"
                    >
                        <span>Explore AI & Writing Tools</span> →
                    </button>
                </div>

                {/* 4. Webmaster & SEO Tools */}
                <div className="bg-white dark:bg-slate-900 p-6 sm:p-7 rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-sm hover:shadow-md transition-all space-y-4 flex flex-col justify-between">
                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-amber-50 dark:bg-amber-950/80 text-amber-600 flex items-center justify-center font-bold text-lg">
                                🌐
                            </div>
                            <h3 className="text-lg font-black text-slate-900 dark:text-white">Webmaster & SEO Tools</h3>
                        </div>
                        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                            Grow search engine traffic and audit domain health. Simulate Google Desktop & Mobile search snippets with live Open Graph preview, generate clean keyword-only SEO URL slugs, query DNS records (A, MX, TXT, CNAME) via DNS-over-HTTPS, inspect SSL certificate expiry, and analyze HTTP response headers.
                        </p>
                        <div className="flex flex-wrap gap-1.5 pt-1">
                            {['Google SERP Preview', 'SEO Slug Maker', 'DNS Lookup', 'SSL Checker', 'HTTP Headers'].map(name => (
                                <span key={name} className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                                    {name}
                                </span>
                            ))}
                        </div>
                    </div>
                    <button 
                        onClick={() => navigateTo('/tool/serp-preview-generator')} 
                        className="text-xs font-bold text-cyan-600 dark:text-cyan-400 hover:underline flex items-center gap-1 cursor-pointer pt-2"
                    >
                        <span>Explore SEO Tools</span> →
                    </button>
                </div>

                {/* 5. Social Media & Creator */}
                <div className="bg-white dark:bg-slate-900 p-6 sm:p-7 rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-sm hover:shadow-md transition-all space-y-4 flex flex-col justify-between">
                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-pink-50 dark:bg-pink-950/80 text-pink-600 flex items-center justify-center font-bold text-lg">
                                📱
                            </div>
                            <h3 className="text-lg font-black text-slate-900 dark:text-white">Social Media & Creators</h3>
                        </div>
                        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                            Level up your social branding across LinkedIn, Instagram, TikTok, and YouTube. Turn quotes and tweets into HD aesthetic cards, generate 30+ stylish unicode bio fonts, trim and cut MP3 songs with our waveform audio cutter, and extract viral YouTube tags, descriptions, and 4K thumbnails.
                        </p>
                        <div className="flex flex-wrap gap-1.5 pt-1">
                            {['Tweet to Image', 'Fancy Font Generator', 'Audio Cutter', 'YouTube SEO', 'FB Video Downloader'].map(name => (
                                <span key={name} className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                                    {name}
                                </span>
                            ))}
                        </div>
                    </div>
                    <button 
                        onClick={() => navigateTo('/tool/tweet-to-image')} 
                        className="text-xs font-bold text-cyan-600 dark:text-cyan-400 hover:underline flex items-center gap-1 cursor-pointer pt-2"
                    >
                        <span>Explore Creator Tools</span> →
                    </button>
                </div>

                {/* 6. Developer & Code Utilities */}
                <div className="bg-white dark:bg-slate-900 p-6 sm:p-7 rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-sm hover:shadow-md transition-all space-y-4 flex flex-col justify-between">
                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-cyan-50 dark:bg-cyan-950/80 text-cyan-600 flex items-center justify-center font-bold text-lg">
                                💻
                            </div>
                            <h3 className="text-lg font-black text-slate-900 dark:text-white">Developer & Tech Utilities</h3>
                        </div>
                        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                            Essential utilities for modern software engineers and DevOps. Inspect code changes with our Side-by-Side Diff Checker, decode and verify JWT claims with live countdowns, convert cURL commands into JavaScript Fetch/Axios or Python, generate Cron schedule expressions visually, and format JSON/XML/YAML datasets.
                        </p>
                        <div className="flex flex-wrap gap-1.5 pt-1">
                            {['Diff Checker', 'JWT Decoder', 'cURL Converter', 'Cron Generator', 'JSON Formatter'].map(name => (
                                <span key={name} className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                                    {name}
                                </span>
                            ))}
                        </div>
                    </div>
                    <button 
                        onClick={() => navigateTo('/tool/diff-checker')} 
                        className="text-xs font-bold text-cyan-600 dark:text-cyan-400 hover:underline flex items-center gap-1 cursor-pointer pt-2"
                    >
                        <span>Explore Developer Tools</span> →
                    </button>
                </div>

                {/* 7. Financial & Daily Calculators */}
                <div className="bg-white dark:bg-slate-900 p-6 sm:p-7 rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-sm hover:shadow-md transition-all space-y-4 flex flex-col justify-between lg:col-span-3">
                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-emerald-50 dark:bg-emerald-950/80 text-emerald-600 flex items-center justify-center font-bold text-lg">
                                💰
                            </div>
                            <h3 className="text-lg font-black text-slate-900 dark:text-white">Financial, Business & Daily Life Calculators</h3>
                        </div>
                        <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                            Accurate, formula-verified calculators for every aspect of finance and daily life. Calculate freelance hourly and project rates based on target income, calculate road trip petrol costs and split bills among friends, evaluate bank mortgage and loan EMIs, compute GST/VAT, and convert over 160 global currencies with live market exchange rates.
                        </p>
                        <div className="flex flex-wrap gap-2 pt-1">
                            {['Freelance Hourly Rate', 'Trip Fuel & Bill Splitter', 'Loan & Mortgage EMI', 'Profit Margin Calculator', 'Live Currency Converter', 'Age Calculator', 'BMI Calculator'].map(name => (
                                <span key={name} className="text-xs font-semibold px-3 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                                    {name}
                                </span>
                            ))}
                        </div>
                    </div>
                    <button 
                        onClick={() => navigateTo('/tool/freelance-rate-calculator')} 
                        className="text-xs font-bold text-cyan-600 dark:text-cyan-400 hover:underline flex items-center gap-1 cursor-pointer pt-2"
                    >
                        <span>Explore All Calculators</span> →
                    </button>
                </div>
            </div>

            {/* Why ToolsCopilot Stands Apart (4 Pillars) */}
            <div className="p-8 sm:p-10 rounded-[2.5rem] bg-gradient-to-br from-slate-50 via-cyan-50/30 to-slate-100 dark:from-slate-900 dark:via-slate-900/90 dark:to-slate-950 text-slate-900 dark:text-white border border-slate-200/80 dark:border-slate-800 shadow-sm space-y-6">
                <div className="text-center max-w-2xl mx-auto space-y-2">
                    <h3 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900 dark:text-white">
                        Why Millions Choose ToolsCopilot
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                        Built from the ground up to solve the privacy, cost, and speed problems of legacy cloud tool websites.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 pt-2">
                    <div className="p-5 bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-200/80 dark:border-slate-700/60 shadow-xs space-y-2">
                        <div className="text-2xl">🔒</div>
                        <h4 className="text-sm font-black text-slate-900 dark:text-white">100% Client-Side Privacy</h4>
                        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                            Your confidential PDFs, photos, and files are processed strictly inside your device's memory. No files are ever sent to our servers.
                        </p>
                    </div>

                    <div className="p-5 bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-200/80 dark:border-slate-700/60 shadow-xs space-y-2">
                        <div className="text-2xl">⚡</div>
                        <h4 className="text-sm font-black text-slate-900 dark:text-white">Zero Upload Lag</h4>
                        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                            No waiting for slow file uploads or cloud processing queues. Computations happen instantly using local CPU power.
                        </p>
                    </div>

                    <div className="p-5 bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-200/80 dark:border-slate-700/60 shadow-xs space-y-2">
                        <div className="text-2xl">💸</div>
                        <h4 className="text-sm font-black text-slate-900 dark:text-white">100% Free Forever</h4>
                        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                            No premium plans, no hidden paywalls, no watermarks, and no sign-up forms. Unlimited access to all tools.
                        </p>
                    </div>

                    <div className="p-5 bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-200/80 dark:border-slate-700/60 shadow-xs space-y-2">
                        <div className="text-2xl">📱</div>
                        <h4 className="text-sm font-black text-slate-900 dark:text-white">Cross-Device Ready</h4>
                        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                            Works on iPhone, iPad, Android, Windows, Mac, and Linux without installing heavy desktop applications.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};

interface HomepageProps {
    tools: ToolInfo[];
    pdfImageModes: PdfImageModeInfo[];
    navigateTo: (path: string) => void;
}

export const Homepage: React.FC<HomepageProps> = ({ tools, pdfImageModes, navigateTo }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilter, setActiveFilter] = useState<ToolCategory | 'All'>('All');

    const allTools: UnifiedToolInfo[] = useMemo(() => {
        const pdfTools: UnifiedToolInfo[] = pdfImageModes.map(mode => ({ ...mode, isPdfImageMode: true }));
        const mainTools: UnifiedToolInfo[] = tools
            .filter(t => t.category && t.category !== 'Site Pages')
            .map(t => ({ ...t, isPdfImageMode: false }));
        return [...pdfTools, ...mainTools];
    }, [tools, pdfImageModes]);

    const categoryOrder: ToolCategory[] = [
        'Organize PDF', 'Optimize PDF', 'Convert to PDF', 'Convert from PDF', 'Edit PDF', 'PDF Security', 
        'Image Tools', 'Social Media & Video', 'YouTube Tools', 'AI & Writing', 'Web & SEO', 'Text & File Tools', 'Developer Tools', 'Converters', 'Calculators', 'Other'
    ];
    
    const categories = useMemo(() => {
        const active = categoryOrder.filter(c => allTools.some(t => t.category === c && c !== 'Site Pages'));
        const allCats = Array.from(new Set(allTools.map(t => t.category).filter(Boolean))) as ToolCategory[];
        allCats.forEach(c => {
            if (c !== 'Site Pages' && !active.includes(c)) {
                active.push(c);
            }
        });
        return active;
    }, [allTools]);
    
    const categoryCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        allTools.forEach(t => {
            if (t.category) counts[t.category] = (counts[t.category] || 0) + 1;
        });
        return counts;
    }, [allTools]);

    const filteredTools = useMemo(() => {
        return searchTools(allTools, searchQuery);
    }, [allTools, searchQuery]);
    
    const categorizedTools = useMemo(() => {
        const groups: Record<string, UnifiedToolInfo[]> = {};
        filteredTools.forEach(t => {
            if (t.category) {
                if (!groups[t.category]) groups[t.category] = [];
                groups[t.category].push(t);
            }
        });
        return groups;
    }, [filteredTools]);
    
    const categoriesToRender = (activeFilter === 'All' || searchQuery.trim().length > 0)
        ? categories.filter(c => categorizedTools[c]?.length > 0) 
        : (categorizedTools[activeFilter]?.length > 0 ? [activeFilter] : []);

    const popularToolIds = ['pdf-compress', 'image-editor', 'facebook-downloader', 'age-calculator', 'json-formatter', 'cpm-calculator'];
    const popularTools = useMemo(() => {
        return popularToolIds.map(id => allTools.find(t => t.id === id)).filter(Boolean) as UnifiedToolInfo[];
    }, [allTools]);

    return (
        <div className="space-y-12 sm:space-y-16">
            {/* Hero Section */}
            <section className="relative overflow-hidden pt-6 pb-10 px-4 sm:px-8 rounded-3xl bg-gradient-to-b from-cyan-50/40 via-slate-50/30 to-transparent dark:from-cyan-950/25 dark:via-slate-900/30 dark:to-transparent border border-slate-200/60 dark:border-slate-800/40 shadow-xs">
                {/* Animated High-Tech Matrix Dots */}
                <div className="absolute inset-0 hero-animated-dots pointer-events-none -z-10 opacity-70 dark:opacity-50" />

                {/* Ambient Glowing Orbs */}
                <div className="absolute -top-20 -left-20 w-80 h-80 bg-cyan-400/20 dark:bg-cyan-500/15 rounded-full blur-3xl pointer-events-none -z-10 animate-pulse" style={{ animationDuration: '6s' }} />
                <div className="absolute top-1/3 -right-20 w-80 h-80 bg-blue-500/15 dark:bg-blue-600/10 rounded-full blur-3xl pointer-events-none -z-10 animate-pulse" style={{ animationDuration: '8s', animationDelay: '2s' }} />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center relative z-10">
                    <div className="text-center lg:text-left space-y-5">
                        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-teal-500/10 border border-cyan-500/20 px-3.5 py-1 rounded-full text-xs font-bold text-cyan-700 dark:text-cyan-300 shadow-xs">
                            <SparklesIcon className="w-3.5 h-3.5 text-cyan-500 animate-pulse" />
                            <span>140+ Free Online Tools — 100% Client-Side & Private</span>
                        </div>

                        <h1 className="text-3xl sm:text-5xl lg:text-5xl font-black text-slate-900 dark:text-white leading-[1.12] tracking-tight">
                            Free Online Tools for <span className="bg-gradient-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent">PDF, Images & Workflows</span>
                        </h1>

                        <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 font-normal leading-relaxed max-w-xl mx-auto lg:mx-0">
                            Transform, convert, compress, and calculate instantly in your browser. Fast client-side processing, zero file uploads to servers, and completely free forever.
                        </p>

                        <div className="pt-1 flex flex-wrap justify-center lg:justify-start gap-4">
                            <div className="flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-slate-600 dark:text-slate-400">
                                <div className="w-4.5 h-4.5 rounded-full bg-teal-500/10 flex items-center justify-center text-teal-500"><CheckIcon className="w-3 h-3"/></div> No Sign-up
                            </div>
                            <div className="flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-slate-600 dark:text-slate-400">
                                <div className="w-4.5 h-4.5 rounded-full bg-teal-500/10 flex items-center justify-center text-teal-500"><CheckIcon className="w-3 h-3"/></div> 100% Free
                            </div>
                            <div className="flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-slate-600 dark:text-slate-400">
                                <div className="w-4.5 h-4.5 rounded-full bg-teal-500/10 flex items-center justify-center text-teal-500"><CheckIcon className="w-3 h-3"/></div> Zero File Uploads
                            </div>
                        </div>

                        {/* Quick Jump Category Chips */}
                        <div className="pt-2 flex flex-wrap justify-center lg:justify-start gap-1.5">
                            {['Organize PDF', 'Image Tools', 'YouTube Tools', 'Calculators', 'Converters', 'Developer Tools'].map((cat) => (
                                <button
                                    key={cat}
                                    onClick={() => {
                                        setActiveFilter(cat as ToolCategory);
                                        const el = document.getElementById('toolkit-section');
                                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                                    }}
                                    className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-cyan-50 hover:text-cyan-700 dark:hover:bg-slate-700 transition-colors"
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex justify-center items-center min-h-[360px]">
                        <IconSphere />
                    </div>
                </div>
            </section>

            {/* Popular & Trending Tools Bar */}
            <section className="bg-gradient-to-r from-slate-50 to-cyan-50/40 dark:from-slate-900/60 dark:to-cyan-950/20 p-6 sm:p-8 rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-xs">
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <span className="text-[11px] font-bold uppercase tracking-wider text-cyan-600 dark:text-cyan-400">Quick Access</span>
                        <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white">Most Popular Tools</h2>
                    </div>
                    <span className="text-xs font-semibold text-slate-400 dark:text-slate-500">Fast & Verified</span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {popularTools.map(tool => {
                        const Icon = tool.icon;
                        return (
                            <a
                                key={tool.id}
                                href={`/tool/${tool.id}`}
                                onClick={(e) => { e.preventDefault(); navigateTo(`/tool/${tool.id}`); }}
                                className="group flex flex-col items-center text-center p-3.5 sm:p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-cyan-500 hover:shadow-md dark:hover:shadow-cyan-950/40 transition-all duration-300 hover:-translate-y-1"
                            >
                                <div className="w-10 h-10 rounded-lg bg-slate-50 dark:bg-slate-800 flex items-center justify-center mb-2.5 group-hover:scale-110 transition-transform">
                                    <Icon className="w-6.5 h-6.5" />
                                </div>
                                <h3 className="text-xs font-bold text-slate-900 dark:text-white group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors truncate w-full">
                                    {tool.label}
                                </h3>
                                <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate w-full mt-0.5">
                                    {tool.category}
                                </p>
                            </a>
                        );
                    })}
                </div>
            </section>

            {/* Search and Filters Section */}
            <div id="toolkit-section" className="max-w-[90rem] mx-auto px-2 sm:px-4 bg-slate-50/60 dark:bg-slate-900/30 py-10 sm:py-12 rounded-3xl transition-colors duration-300 border border-slate-200/60 dark:border-slate-800">
                <div className="text-center max-w-3xl mx-auto mb-8">
                    <h2 className="text-2xl sm:text-4xl font-black text-slate-900 dark:text-white mb-2 tracking-tight">
                        Explore the Full Toolkit
                    </h2>
                    <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base">
                        Search over 140+ utilities or filter by category to find exactly what you need in seconds.
                    </p>
                </div>
                
                {/* Search Bar */}
                <div className="relative mb-6 max-w-xl mx-auto">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                        <SearchIcon className="w-5 h-5" />
                    </div>
                    <input 
                        type="text" 
                        value={searchQuery} 
                        onChange={(e) => setSearchQuery(e.target.value)} 
                        placeholder="Search any tool (e.g. PDF Merge, Barcode, Resize Photo, Age Calculator)..." 
                        className="w-full pl-12 pr-10 py-3.5 text-sm sm:text-base bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-2xl shadow-xs border border-slate-200 dark:border-slate-700 focus:ring-3 focus:ring-cyan-500/10 focus:border-cyan-500 outline-none transition-all" 
                    />
                    {searchQuery && (
                        <button 
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 flex items-center justify-center text-xs font-bold transition-colors"
                            title="Clear search"
                        >
                            ✕
                        </button>
                    )}
                </div>

                {/* Search Active Indicator */}
                {searchQuery.trim() && (
                    <div className="flex items-center justify-center gap-3 mb-6 animate-fade-in-up">
                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-300 bg-cyan-50 dark:bg-cyan-950/60 border border-cyan-200 dark:border-cyan-900 px-4 py-1.5 rounded-full">
                            Found <strong className="text-cyan-600 dark:text-cyan-400">{filteredTools.length}</strong> {filteredTools.length === 1 ? 'tool' : 'tools'} matching "<span className="text-slate-900 dark:text-white font-bold">{searchQuery}</span>"
                        </span>
                        <button 
                            onClick={() => setSearchQuery('')} 
                            className="text-xs font-bold text-cyan-600 dark:text-cyan-400 hover:underline"
                        >
                            Reset
                        </button>
                    </div>
                )}

                {/* Category Filters */}
                <div className="flex justify-center flex-wrap gap-2 mb-10">
                    <button 
                        onClick={() => setActiveFilter('All')} 
                        className={`px-3.5 py-1.5 text-xs font-bold rounded-xl transition-all border flex items-center gap-1.5 ${
                            activeFilter === 'All' 
                                ? 'bg-cyan-600 text-white border-cyan-600 shadow-xs scale-105' 
                                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                    >
                        <span>All Tools</span>
                        <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${activeFilter === 'All' ? 'bg-white/20 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'}`}>
                            {allTools.length}
                        </span>
                    </button>
                    {categories.map(c => (
                        <button 
                            key={c} 
                            onClick={() => setActiveFilter(c)} 
                            className={`px-3.5 py-1.5 text-xs font-bold rounded-xl transition-all border flex items-center gap-1.5 ${
                                activeFilter === c 
                                ? 'bg-cyan-600 text-white border-cyan-600 shadow-xs scale-105' 
                                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
                            }`}
                        >
                            <span>{c}</span>
                            <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${activeFilter === c ? 'bg-white/20 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'}`}>
                                {categoryCounts[c] || 0}
                            </span>
                        </button>
                    ))}
                </div>

                {/* Tool Grids in 4 Columns */}
                <div className="space-y-12">
                    {categoriesToRender.length > 0 ? categoriesToRender.map(cat => (
                        <div key={cat}>
                            <div className="flex items-center gap-3 mb-6 px-1">
                                <h3 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white">{cat}</h3>
                                <div className="h-px flex-grow bg-slate-200 dark:bg-slate-800"></div>
                                <span className="text-[11px] font-bold text-slate-400">{categorizedTools[cat]?.length || 0} tools</span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 px-1">
                                {categorizedTools[cat].map(t => <ToolCard key={t.id} tool={t} navigateTo={navigateTo} />)}
                            </div>
                        </div>
                    )) : (
                        <div className="text-center py-16 bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 p-8 space-y-4 max-w-2xl mx-auto">
                            <div className="text-4xl">🔍</div>
                            <h3 className="text-slate-900 dark:text-white font-black text-xl">No tools found matching "{searchQuery}"</h3>
                            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                                We couldn't find an exact match, but you can try searching for general terms like <strong>"PDF"</strong>, <strong>"Image"</strong>, <strong>"Video"</strong>, <strong>"Calculator"</strong>, or browse popular tools below:
                            </p>
                            <div className="flex flex-wrap justify-center gap-2 pt-2">
                                {['PDF Merge', 'PDF Compress', 'Barcode Generator', 'Image to PDF', 'Background Remover', 'Age Calculator'].map(term => (
                                    <button 
                                        key={term} 
                                        onClick={() => setSearchQuery(term)} 
                                        className="text-xs font-semibold px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-cyan-50 dark:hover:bg-cyan-950/60 hover:text-cyan-600 dark:hover:text-cyan-300 rounded-xl transition-all"
                                    >
                                        {term}
                                    </button>
                                ))}
                            </div>
                            <button onClick={() => setSearchQuery('')} className="mt-4 px-5 py-2.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-cyan-600/20">
                                View All 140+ Tools
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* 6 High-Converting Visual Feature Showcases */}
            <div className="max-w-7xl mx-auto space-y-12">
                
                {/* Showcase 1: The Modern PDF Toolkit */}
                <section className="p-8 sm:p-12 rounded-[2.5rem] bg-gradient-to-br from-slate-50 to-cyan-50/40 dark:from-slate-900/90 dark:to-cyan-950/30 border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col lg:flex-row items-center gap-10">
                    <div className="flex-1 space-y-5 text-center lg:text-left">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-100 dark:bg-cyan-950/80 text-cyan-700 dark:text-cyan-300 text-xs font-bold uppercase tracking-wider">
                            <SparklesIcon className="w-3.5 h-3.5" /> High-Performance PDF Suite
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white leading-tight">
                            Compress, Merge & Edit PDFs at Blazing Speed
                        </h2>
                        <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed">
                            Shrink oversized documents by up to 85% for email attachments, merge multi-page contracts, add electronic signatures, and password-protect sensitive records—all processed 100% client-side.
                        </p>
                        <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 pt-2">
                            <button 
                                onClick={() => navigateTo('/tool/pdf-compress')}
                                className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl font-bold text-sm shadow-md shadow-cyan-600/20 transition-all hover:scale-105"
                            >
                                Compress PDF Now
                            </button>
                            <button 
                                onClick={() => navigateTo('/tool/pdf-merge')}
                                className="px-6 py-3 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-xl font-bold text-sm border border-slate-200 dark:border-slate-700 transition-all"
                            >
                                Merge Documents
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 w-full flex justify-center items-center py-4">
                        <div className="relative w-full max-w-sm h-72 flex items-center justify-center">
                            <div className="absolute top-2 -left-2 bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-lg -rotate-6 w-64 opacity-75 transition-transform hover:-rotate-12 duration-300">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="w-8 h-8 rounded-lg bg-red-500/10 text-red-500 flex items-center justify-center font-bold text-xs">PDF</div>
                                    <div className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">Financial_Report_Q4.pdf</div>
                                </div>
                                <div className="text-[11px] text-slate-400">Original: 48.2 MB</div>
                            </div>

                            <div className="absolute top-8 right-0 bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-xl rotate-6 w-64 z-10 transition-transform hover:rotate-12 duration-300">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="w-8 h-8 rounded-lg bg-blue-500/10 text-blue-500 flex items-center justify-center font-bold text-xs">DOC</div>
                                    <div className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">Client_Agreement.pdf</div>
                                </div>
                                <div className="text-[11px] text-emerald-500 font-bold">🔒 AES 256-bit Encrypted</div>
                            </div>

                            <div className="relative bg-gradient-to-br from-slate-900 to-slate-950 text-white p-6 rounded-3xl border-2 border-cyan-500/50 shadow-2xl z-20 w-72 transform hover:scale-105 transition-all duration-300">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-xl bg-cyan-500 text-slate-950 flex items-center justify-center font-black text-xs">✓</div>
                                        <span className="text-xs font-bold text-cyan-300">Optimized</span>
                                    </div>
                                    <span className="text-xs font-extrabold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">-84% Reduction</span>
                                </div>
                                <h4 className="text-base font-bold text-white mb-1">Commercial_Proposal.pdf</h4>
                                <div className="flex justify-between text-xs text-slate-400 mt-3 pt-3 border-t border-slate-800">
                                    <span>Was: 22.4 MB</span>
                                    <span className="text-cyan-400 font-bold">Now: 3.5 MB</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Showcase 2: AI Image Studio & Format Remastering */}
                <section className="p-8 sm:p-12 rounded-[2.5rem] bg-gradient-to-br from-slate-50 to-blue-50/40 dark:from-slate-900/90 dark:to-blue-950/30 border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col lg:flex-row-reverse items-center gap-10">
                    <div className="flex-1 space-y-5 text-center lg:text-left">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-950/80 text-blue-700 dark:text-blue-300 text-xs font-bold uppercase tracking-wider">
                            <SparklesIcon className="w-3.5 h-3.5" /> Browser-Based Image Studio
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white leading-tight">
                            Enhance, Crop, Upscale & Convert Photos Locally
                        </h2>
                        <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed">
                            Convert photos to modern next-gen WebP formats, crop to custom social aspect ratios, upscale graphics with AI, and remove backgrounds with zero third-party cloud uploads.
                        </p>
                        <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 pt-2">
                            <button 
                                onClick={() => navigateTo('/tool/image-editor')}
                                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-xl font-bold text-sm shadow-md shadow-blue-600/20 transition-all hover:scale-105"
                            >
                                Open Image Studio
                            </button>
                            <button 
                                onClick={() => navigateTo('/tool/image-format-converter')}
                                className="px-6 py-3 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-xl font-bold text-sm border border-slate-200 dark:border-slate-700 transition-all"
                            >
                                Convert to WebP / SVG
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 w-full flex justify-center items-center py-4">
                        <ImageComparisonSlider />
                    </div>
                </section>

                {/* Showcase 3: Developer & Code Workbench */}
                <section className="p-8 sm:p-12 rounded-[2.5rem] bg-gradient-to-br from-slate-50 to-indigo-50/40 dark:from-slate-900/90 dark:to-indigo-950/30 border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col lg:flex-row items-center gap-10">
                    <div className="flex-1 space-y-5 text-center lg:text-left">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300 text-xs font-bold uppercase tracking-wider">
                            <CodeBracketIcon className="w-3.5 h-3.5" /> Developer & Code Suite
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white leading-tight">
                            Format, Validate & Beautify JSON, HTML & Code
                        </h2>
                        <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed">
                            Clean unformatted JSON responses, beautify minified HTML/CSS code, calculate SHA-256 / MD5 cryptographic hashes, and decode Base64 data with real-time syntax checking.
                        </p>
                        <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 pt-2">
                            <button 
                                onClick={() => navigateTo('/tool/json-formatter')}
                                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm shadow-md shadow-indigo-600/20 transition-all hover:scale-105"
                            >
                                Launch JSON Formatter
                            </button>
                            <button 
                                onClick={() => navigateTo('/tool/html-beautifier')}
                                className="px-6 py-3 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-xl font-bold text-sm border border-slate-200 dark:border-slate-700 transition-all"
                            >
                                HTML Beautifier
                            </button>
                        </div>
                    </div>

                    {/* Futuristic IDE Terminal Mockup */}
                    <div className="flex-1 w-full flex justify-center items-center py-4">
                        <div className="w-full max-w-md bg-slate-950 rounded-2xl border border-slate-800 shadow-2xl p-4 font-mono text-xs text-left transform hover:scale-[1.02] transition-transform">
                            {/* Window Header */}
                            <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-800/80">
                                <div className="flex items-center gap-1.5">
                                    <div className="w-3 h-3 rounded-full bg-rose-500" />
                                    <div className="w-3 h-3 rounded-full bg-amber-500" />
                                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                                </div>
                                <span className="text-[11px] text-slate-500 dark:text-slate-400">payload.config.json</span>
                                <span className="text-[9px] px-2 py-0.5 rounded bg-emerald-950 text-emerald-400 font-bold border border-emerald-800">
                                    ✓ Valid JSON
                                </span>
                            </div>
                            {/* Code Lines */}
                            <div className="space-y-1 text-slate-300">
                                <p><span className="text-slate-600 dark:text-slate-400 select-none mr-3">1</span><span className="text-pink-400">{'{'}</span></p>
                                <p><span className="text-slate-600 dark:text-slate-400 select-none mr-3">2</span>  <span className="text-cyan-400">"application"</span>: <span className="text-emerald-300">"ToolsCopilot"</span>,</p>
                                <p><span className="text-slate-600 dark:text-slate-400 select-none mr-3">3</span>  <span className="text-cyan-400">"totalTools"</span>: <span className="text-amber-300">140</span>,</p>
                                <p><span className="text-slate-600 dark:text-slate-400 select-none mr-3">4</span>  <span className="text-cyan-400">"clientSideExecution"</span>: <span className="text-indigo-400">true</span>,</p>
                                <p><span className="text-slate-600 dark:text-slate-400 select-none mr-3">5</span>  <span className="text-cyan-400">"serverLatencyMs"</span>: <span className="text-amber-300">0</span></p>
                                <p><span className="text-slate-600 dark:text-slate-400 select-none mr-3">6</span><span className="text-pink-400">{'}'}</span></p>
                            </div>
                            <div className="mt-3 pt-2.5 border-t border-slate-900 flex justify-between items-center text-[10px] text-slate-500 dark:text-slate-400">
                                <span>Encoding: UTF-8</span>
                                <span className="text-cyan-400 font-bold">Size: 184 bytes (-62% Minified)</span>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Showcase 4: Smart YouTube Creator Studio */}
                <section className="p-8 sm:p-12 rounded-[2.5rem] bg-gradient-to-br from-slate-50 to-rose-50/40 dark:from-slate-900/90 dark:to-rose-950/30 border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col lg:flex-row-reverse items-center gap-10">
                    <div className="flex-1 space-y-5 text-center lg:text-left">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-100 dark:bg-rose-950/80 text-rose-700 dark:text-rose-300 text-xs font-bold uppercase tracking-wider">
                            <SparklesIcon className="w-3.5 h-3.5" /> YouTube Growth & SEO
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white leading-tight">
                            Extract Viral SEO Tags & Grab 4K Thumbnails
                        </h2>
                        <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed">
                            Analyze competitor metadata, generate high-ranking search tags, discover trending video topics, and download crisp maximum-resolution thumbnails directly from any YouTube link.
                        </p>
                        <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 pt-2">
                            <button 
                                onClick={() => navigateTo('/tool/youtube-tag-extractor')}
                                className="px-6 py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold text-sm shadow-md shadow-rose-600/20 transition-all hover:scale-105"
                            >
                                Extract Video Tags
                            </button>
                            <button 
                                onClick={() => navigateTo('/tool/youtube-thumbnail-downloader')}
                                className="px-6 py-3 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-xl font-bold text-sm border border-slate-200 dark:border-slate-700 transition-all"
                            >
                                Download 4K Thumbnail
                            </button>
                        </div>
                    </div>

                    {/* YouTube Creator Card Visual */}
                    <div className="flex-1 w-full flex justify-center items-center py-4">
                        <div className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-3xl p-5 border border-slate-200/80 dark:border-slate-800 shadow-2xl space-y-3.5">
                            {/* Thumbnail Mock */}
                            <div className="relative aspect-video rounded-2xl overflow-hidden bg-slate-900">
                                <img src="https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=600&auto=format&fit=crop" alt="Thumbnail Preview" className="w-full h-full object-cover" />
                                <div className="absolute top-2.5 left-2.5 bg-rose-600 text-white font-black text-[10px] uppercase px-2 py-0.5 rounded-md shadow-md">
                                    4K Ultra HD
                                </div>
                                <div className="absolute bottom-2.5 right-2.5 bg-black/80 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                                    14:28
                                </div>
                            </div>
                            {/* Tag Chips */}
                            <div>
                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1.5">Extracted Tags:</span>
                                <div className="flex flex-wrap gap-1.5">
                                    {['#ViralTrends', '#SEO2026', '#AItools', '#CreatorTips', '#Rank1'].map((tag, i) => (
                                        <span key={i} className="text-xs font-semibold px-2 py-0.5 rounded-lg bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-900/60">
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Showcase: High-Speed Facebook Video, Reel & Shorts Downloader */}
                <section className="p-8 sm:p-12 rounded-[2.5rem] bg-gradient-to-br from-slate-50 to-blue-50/40 dark:from-slate-900/90 dark:to-blue-950/30 border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col lg:flex-row items-center gap-10">
                    <div className="flex-1 space-y-5 text-center lg:text-left">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-950/80 text-blue-700 dark:text-blue-300 text-xs font-bold uppercase tracking-wider">
                            <ColorfulFacebookIcon className="w-4 h-4" /> Facebook Media Suite
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white leading-tight">
                            Download Facebook Videos, Reels & Shorts in 1080p HD
                        </h2>
                        <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed">
                            Just paste any public Facebook URL to download high-speed MP4 videos with live file size estimates. Supports standard videos, mobile reels, shorts, and stories with 100% free unlimited downloads.
                        </p>

                        {/* Quick Paste Widget on Homepage */}
                        <div className="bg-white dark:bg-slate-950 p-2 sm:p-2.5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-md flex flex-col sm:flex-row gap-2 max-w-lg mx-auto lg:mx-0">
                            <input
                                type="url"
                                placeholder="Paste Facebook video or reel link..."
                                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white rounded-xl text-xs sm:text-sm border border-slate-200 dark:border-slate-700 outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                                id="homepage-fb-url"
                            />
                            <button
                                onClick={() => {
                                    navigateTo('/tool/facebook-downloader');
                                }}
                                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs sm:text-sm rounded-xl flex items-center justify-center gap-2 shadow-md shadow-blue-600/30 transition-all hover:scale-105 flex-shrink-0 cursor-pointer"
                            >
                                <DownloadIcon className="w-4 h-4" />
                                <span>Download MP4</span>
                            </button>
                        </div>

                        <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 pt-1">
                            <button 
                                onClick={() => navigateTo('/tool/facebook-downloader')}
                                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl font-bold text-sm shadow-md shadow-blue-600/20 transition-all hover:scale-105"
                            >
                                Open Full Downloader
                            </button>
                            <button 
                                onClick={() => navigateTo('/tool/instagram-downloader')}
                                className="px-6 py-3 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-xl font-bold text-sm border border-slate-200 dark:border-slate-700 transition-all"
                            >
                                Instagram Downloader
                            </button>
                        </div>
                    </div>

                    {/* Facebook Media Card Mockup */}
                    <div className="flex-1 w-full flex justify-center items-center py-4">
                        <div className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-3xl p-5 border border-slate-200/80 dark:border-slate-800 shadow-2xl space-y-4 text-left">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2.5">
                                    <div className="w-8 h-8 rounded-xl bg-blue-600 text-white flex items-center justify-center font-bold">
                                        <ColorfulFacebookIcon className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <div className="text-xs font-black text-slate-900 dark:text-white">Facebook Reel</div>
                                        <div className="text-[10px] text-slate-400">Viral Short Video • 1080p HD</div>
                                    </div>
                                </div>
                                <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300">
                                    Ready
                                </span>
                            </div>

                            {/* Video Thumbnail Preview */}
                            <div className="relative aspect-video rounded-2xl overflow-hidden bg-slate-900 border border-slate-200 dark:border-slate-800">
                                <img 
                                    src="https://images.unsplash.com/photo-1516251193007-45ef944ab0c6?q=80&w=600&auto=format&fit=crop" 
                                    alt="Facebook Video Preview" 
                                    className="w-full h-full object-cover" 
                                />
                                <div className="absolute top-2.5 left-2.5 bg-blue-600 text-white font-black text-[10px] uppercase px-2.5 py-0.5 rounded-md shadow-md flex items-center gap-1">
                                    <span>1080p Full HD</span>
                                </div>
                                <div className="absolute bottom-2.5 right-2.5 bg-black/80 text-white text-[10px] font-bold px-2 py-0.5 rounded-md">
                                    0:45
                                </div>
                            </div>

                            {/* Quality Options */}
                            <div className="space-y-2 pt-1">
                                <div className="flex items-center justify-between p-2.5 rounded-xl bg-blue-50/70 dark:bg-blue-950/40 border border-blue-200/80 dark:border-blue-900/60">
                                    <div className="flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                        <span className="text-xs font-black text-slate-800 dark:text-slate-200">1080p HD Video</span>
                                    </div>
                                    <span className="text-xs font-bold text-blue-700 dark:text-blue-300">~14.5 MB</span>
                                </div>
                                <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                                    <div className="flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                                        <span className="text-xs font-black text-slate-700 dark:text-slate-300">480p SD Video</span>
                                    </div>
                                    <span className="text-xs font-bold text-slate-500 dark:text-slate-400">~5.8 MB</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Showcase 5: Precision Financial & Conversion Calculators */}
                <section className="p-8 sm:p-12 rounded-[2.5rem] bg-gradient-to-br from-slate-50 to-emerald-50/40 dark:from-slate-900/90 dark:to-emerald-950/30 border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col lg:flex-row items-center gap-10">
                    <div className="flex-1 space-y-5 text-center lg:text-left">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-300 text-xs font-bold uppercase tracking-wider">
                            <SparklesIcon className="w-3.5 h-3.5" /> Financial & Business Suite
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white leading-tight">
                            Accurate Loan EMI, Profit Margin & Currency Rates
                        </h2>
                        <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed">
                            Compute mortgage amortizations, evaluate commercial retail markups, calculate compound interest schedules, and convert over 160 global currencies instantly with verified formulas.
                        </p>
                        <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 pt-2">
                            <button 
                                onClick={() => navigateTo('/tool/loan-calculator')}
                                className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-sm shadow-md shadow-emerald-600/20 transition-all hover:scale-105"
                            >
                                Calculate Loan EMI
                            </button>
                            <button 
                                onClick={() => navigateTo('/tool/currency')}
                                className="px-6 py-3 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-xl font-bold text-sm border border-slate-200 dark:border-slate-700 transition-all"
                            >
                                Live Currency Converter
                            </button>
                        </div>
                    </div>

                    {/* Financial Widget Mockup */}
                    <div className="flex-1 w-full flex justify-center items-center py-4">
                        <div className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-3xl p-6 border border-slate-200/80 dark:border-slate-800 shadow-2xl space-y-4 text-left">
                            <div className="flex justify-between items-center">
                                <span className="text-xs font-bold text-slate-400 uppercase">Monthly Loan Breakdown</span>
                                <span className="text-xs font-extrabold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400">Fixed 6.5%</span>
                            </div>
                            <div>
                                <div className="text-3xl font-black text-slate-900 dark:text-white">$462.50 <span className="text-xs font-medium text-slate-400">/ month</span></div>
                            </div>
                            {/* Bar breakdown */}
                            <div className="space-y-1.5">
                                <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-3 flex overflow-hidden">
                                    <div className="bg-emerald-500 h-full" style={{ width: '78%' }} />
                                    <div className="bg-cyan-500 h-full" style={{ width: '22%' }} />
                                </div>
                                <div className="flex justify-between text-[11px] text-slate-500 dark:text-slate-400 font-semibold pt-1">
                                    <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500"/> Principal: $25,000</span>
                                    <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-cyan-500"/> Interest: $2,750</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Showcase 6: Nostalgic Polaroid & Meme Generator */}
                <section className="p-8 sm:p-12 rounded-[2.5rem] bg-gradient-to-br from-slate-50 to-amber-50/40 dark:from-slate-900/90 dark:to-amber-950/20 border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col lg:flex-row-reverse items-center gap-10">
                    <div className="flex-1 space-y-5 text-center lg:text-left">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-950/80 text-amber-700 dark:text-amber-300 text-xs font-bold uppercase tracking-wider">
                            <SparklesIcon className="w-3.5 h-3.5" /> Creative & Social Tools
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white leading-tight">
                            Craft Iconic Polaroids & Viral Memes in Seconds
                        </h2>
                        <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed">
                            Turn your modern camera shots into vintage retro instant film polaroids with custom handwritten cursive notes, or generate top-and-bottom Impact text memes ready for viral sharing.
                        </p>
                        <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 pt-2">
                            <button 
                                onClick={() => navigateTo('/tool/polaroid-maker')}
                                className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-slate-950 font-black rounded-xl text-sm shadow-md shadow-amber-500/20 transition-all hover:scale-105"
                            >
                                Create Instant Polaroid
                            </button>
                            <button 
                                onClick={() => navigateTo('/tool/meme-generator')}
                                className="px-6 py-3 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-xl font-bold text-sm border border-slate-200 dark:border-slate-700 transition-all"
                            >
                                Meme Generator
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 w-full flex justify-center items-center py-4">
                        <div className="relative w-full max-w-sm h-72 flex items-center justify-center">
                            <div className="absolute bg-white dark:bg-slate-800 p-3 pb-10 shadow-xl -rotate-12 w-52 z-10 border border-slate-200 dark:border-slate-700 transition-transform duration-500 hover:-rotate-16 hover:-translate-x-4">
                                <img src="https://images.unsplash.com/photo-1509631179647-0177331693ae?q=80&w=400&auto=format&fit=crop" className="w-full aspect-square object-cover mb-2 rounded" alt="Polaroid 1" />
                                <p className="font-['Caveat'] text-base text-slate-700 dark:text-slate-300 text-center">Summer Memories</p>
                            </div>
                            <div className="absolute bg-white dark:bg-slate-800 p-3.5 pb-12 shadow-2xl rotate-6 w-56 z-20 border border-slate-200 dark:border-slate-700 transition-transform duration-500 hover:rotate-2 hover:scale-105">
                                <img src="https://images.unsplash.com/photo-1539109136881-3be0616acf4b?q=80&w=600&auto=format&fit=crop" className="w-full aspect-square object-cover mb-2 rounded" alt="Polaroid 2" />
                                <p className="font-['Caveat'] text-lg text-slate-800 dark:text-slate-100 text-center font-bold">Fashion Mood ✨</p>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            {/* Feature Item Cards Matrix */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-6xl mx-auto py-6">
                <div className="flex flex-col items-center text-center p-6 sm:p-7 bg-white dark:bg-slate-900 rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-sm hover:shadow-md transition-all">
                    <div className="w-12 h-12 rounded-2xl bg-teal-50 dark:bg-teal-950/60 text-teal-600 dark:text-teal-400 flex items-center justify-center mb-4 border border-teal-100 dark:border-teal-900/40">
                        <LockClosedIcon className="w-6 h-6"/>
                    </div>
                    <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-2">100% Client-Side Privacy</h4>
                    <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm leading-relaxed">Your files never leave your device. All document and image operations execute inside your browser RAM.</p>
                </div>

                <div className="flex flex-col items-center text-center p-6 sm:p-7 bg-white dark:bg-slate-900 rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-sm hover:shadow-md transition-all">
                    <div className="w-12 h-12 rounded-2xl bg-cyan-50 dark:bg-cyan-950/60 text-cyan-600 dark:text-cyan-400 flex items-center justify-center mb-4 border border-cyan-100 dark:border-cyan-900/40">
                        <SparklesIcon className="w-6 h-6"/>
                    </div>
                    <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Zero Network Latency</h4>
                    <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm leading-relaxed">No upload progress bars or cloud queuing. Process large files instantaneously using your device's native CPU.</p>
                </div>

                <div className="flex flex-col items-center text-center p-6 sm:p-7 bg-white dark:bg-slate-900 rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-sm hover:shadow-md transition-all">
                    <div className="w-12 h-12 rounded-2xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center mb-4 border border-indigo-100 dark:border-indigo-900/40">
                        <CheckIcon className="w-6 h-6"/>
                    </div>
                    <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-2">100% Free Forever</h4>
                    <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm leading-relaxed">Full unrestricted access to all 140+ tools without subscriptions, daily credits, or paywall barriers.</p>
                </div>
            </section>


            {/* Welcome & In-Depth Tool Guide Section */}
            <WelcomeAboutSection navigateTo={navigateTo} />

            {/* Interactive Expanded Accordion FAQ */}
            <PlatformFAQ />
        </div>
    );
};
